/**
 * The workspace's words and derived facts (docs/ux/workspace.md): the
 * patient title, the mode, the attention groups, the fold labels and the
 * event copy. Pure functions so the screens and the specs share one source.
 */

import { statusLabel, type Status } from '@beam/core/status';
import type { HeardWord, WorkspaceBox, WorkspaceEventKind, WorkspaceEventOption, WorkspaceRole, WorkspaceSection, WorkspaceView } from '@/app/types';
import { longDate, pluralise } from '@/lib/format';

export type Mode = 'edit' | 'read';

/** The persona owns the round when the status waits on it; the report is read only once complete. */
export function modeFor(view: WorkspaceView): Mode {
  if (!view.reachable || view.status === 'qa_complete') return 'read';
  if (view.actorRole === 'doctor') return view.status === 'awaiting_doctor_draft' || view.status === 'awaiting_doctor_review' ? 'edit' : 'read';
  return view.status === 'awaiting_qa' ? 'edit' : 'read';
}

export function patientTitle(view: Pick<WorkspaceView, 'patient' | 'matterRef'>): string {
  const surname = view.patient.surname.trim();
  if (!surname) return view.matterRef;
  return `${surname.toUpperCase()} ${view.patient.given}`.trim();
}

export function doctorSentence(view: Pick<WorkspaceView, 'reportTypeLabel' | 'doctor'>): string {
  const specialty = view.doctor.specialty ? `, ${view.doctor.specialty.charAt(0).toUpperCase()}${view.doctor.specialty.slice(1)}` : '';
  return `${view.reportTypeLabel} for ${view.doctor.name}${specialty}.`;
}

export function waitingSentence(view: Pick<WorkspaceView, 'waitingOn' | 'actorRole'>): string {
  if (view.waitingOn === 'nobody') return 'Waiting on nobody';
  return view.waitingOn === view.actorRole ? 'Waiting on you' : view.waitingOn === 'doctor' ? 'Waiting on the doctor' : 'Waiting on QA';
}

export function finalisedSentence(finalisedAt: string | null): string {
  return finalisedAt ? `Finalised ${longDate(finalisedAt)}` : 'Not yet finalised';
}

/** Why the boxes are read only on the other persona's turn, or when the report is complete. */
export function readReason(view: WorkspaceView): string | null {
  if (view.status === 'qa_complete') return 'This report is complete. Nothing here can be edited; the preview and export remain.';
  if (view.actorRole === 'doctor' && view.status === 'awaiting_qa') return 'With QA: QA is reviewing this round. Editing resumes when QA sends it back.';
  if (view.actorRole === 'qa' && view.status !== 'awaiting_qa') {
    return view.status === 'awaiting_doctor_draft' ? 'With the doctor: the draft is being written. Proposing starts when the doctor submits it.' : 'With the doctor: the suggestions are being decided. Proposing resumes when the doctor resubmits.';
  }
  return null;
}

export function unreachableCopy(view: Pick<WorkspaceView, 'matterRef' | 'matterStatus'>): { title: string; detail: string } {
  const at = `${view.matterRef} is at ${view.matterStatus} ${statusLabel(view.matterStatus)}.`;
  if (view.matterStatus < 6) return { title: 'The workspace opens when the case reaches 6 Awaiting Doctor Draft.', detail: at };
  if (view.matterStatus === 13) return { title: 'The workspace is paused with the case.', detail: `${at} The report can be read and written again when the case resumes at 6 Awaiting Doctor Draft.` };
  return { title: 'The workspace is closed with the case.', detail: at };
}

/* ------------------------------------------------------------- events */

export const EVENT_ORDER: Record<WorkspaceRole, WorkspaceEventKind[]> = {
  doctor: ['doctor_submits', 'doctor_resubmits'],
  qa: ['qa_submits', 'qa_completes'],
};

export const EVENT_BUSY: Record<WorkspaceEventKind, string> = {
  doctor_submits: 'Submitting',
  qa_submits: 'Sending',
  doctor_resubmits: 'Resubmitting',
  qa_completes: 'Completing',
};

/** The events the status offers this persona (the hub's forward list): an event another status owns is not shown refused, it is absent. */
const OFFERED: Record<WorkspaceView['status'], WorkspaceEventKind[]> = {
  awaiting_doctor_draft: ['doctor_submits'],
  awaiting_qa: ['qa_submits', 'qa_completes'],
  awaiting_doctor_review: ['doctor_resubmits'],
  qa_complete: [],
};

export function eventsFor(view: WorkspaceView): WorkspaceEventOption[] {
  return EVENT_ORDER[view.actorRole]
    .filter((kind) => OFFERED[view.status].includes(kind))
    .map((kind) => view.events.find((e) => e.kind === kind))
    .filter((e): e is WorkspaceEventOption => Boolean(e));
}

/** The first allowed event in the persona's order is the ink primary; the engine's line sits under each. */
export function primaryEvent(view: WorkspaceView): WorkspaceEventOption | null {
  return eventsFor(view).find((e) => e.allowed) ?? null;
}

export function engineLine(e: WorkspaceEventOption): string {
  if (e.allowed && e.to !== undefined) return `to ${e.to} ${statusLabel(e.to)}`;
  return `Refused: ${e.reason ?? 'not available'}`;
}

export function movedToast(kind: WorkspaceEventKind, matterRef: string, to: Status): string {
  const moved = `${matterRef} moved to ${to} ${statusLabel(to)}`;
  switch (kind) {
    case 'doctor_submits': return `Submitted. ${moved}.`;
    case 'qa_submits': return `Sent to the doctor. ${moved}.`;
    case 'doctor_resubmits': return `Resubmitted. ${moved}.`;
    case 'qa_completes': return `QA complete. ${moved} and is finalised.`;
  }
}

/* ---------------------------------------------------------- attention */

export type AttentionKey = 'suggestions' | 'clarifications' | 'applied' | 'changes' | 'empty' | 'notices';

export type AttentionGroup = { key: AttentionKey; one: string; many: string; boxIds: string[] };

export function allBoxes(view: WorkspaceView): WorkspaceBox[] {
  return view.sections.flatMap((s) => s.boxes);
}

/** The groups in order, non-empty only; QA sees doctor changes, the doctor sees applied changes (D-0040). */
export function attentionGroups(view: WorkspaceView): AttentionGroup[] {
  const boxes = allBoxes(view);
  const role = view.actorRole;
  const groups: AttentionGroup[] = [
    { key: 'suggestions', one: role === 'doctor' ? 'Open suggestion' : 'Suggestion awaiting the doctor', many: role === 'doctor' ? 'Open suggestions' : 'Suggestions awaiting the doctor', boxIds: boxes.filter((b) => b.suggestions.length > 0).map((b) => b.id) },
    { key: 'clarifications', one: 'Question to answer', many: 'Questions to answer', boxIds: boxes.filter((b) => b.clarifications.length > 0).map((b) => b.id) },
    ...(role === 'doctor' ? [{ key: 'applied' as const, one: 'Applied by QA', many: 'Applied by QA', boxIds: boxes.filter((b) => b.applied.length > 0).map((b) => b.id) }] : []),
    ...(role === 'qa' ? [{ key: 'changes' as const, one: 'Doctor change to review', many: 'Doctor changes to review', boxIds: boxes.filter((b) => b.doctorChange !== null).map((b) => b.id) }] : []),
    { key: 'empty', one: 'Empty box', many: 'Empty boxes', boxIds: boxes.filter((b) => b.editable && b.text.trim() === '').map((b) => b.id) },
    { key: 'notices', one: 'Noticed', many: 'Noticed', boxIds: boxes.filter((b) => b.notices.length > 0).map((b) => b.id) },
  ];
  return groups.filter((g) => g.boxIds.length > 0);
}

export function groupLabel(g: AttentionGroup): string {
  return g.boxIds.length === 1 ? g.one : g.many;
}

/* ------------------------------------------------------------ folding */

/** "2(a)" and "2(b)" belong to group "2"; the label up to the first "(" or ".". */
export function questionNumberOf(box: WorkspaceBox): string {
  const m = /^[^(.]+/.exec(box.label);
  return (m ? m[0] : box.label).trim();
}

export function foldLabel(section: WorkspaceSection, folded: boolean): string {
  if (!folded) return 'Collapse';
  const n = section.boxes.filter((b) => b.editable).length;
  if (section.kind === 'questions') return `Show ${pluralise(n, 'question')}`;
  if (section.kind === 'fields') return `Show ${pluralise(Object.keys(section.boxes).length || 1, 'field')}`;
  return n === 0 ? 'Show section' : `Show ${pluralise(n, 'box', 'boxes')}`;
}

export function sectionMeta(section: WorkspaceSection): string[] {
  const parts: string[] = [];
  if (section.renamedFrom) parts.push(`renamed from ${section.renamedFrom}`);
  if (section.round > 0) parts.push(`QA round ${section.round}`);
  if (section.openSuggestions > 0) parts.push(pluralise(section.openSuggestions, 'open suggestion'));
  return parts;
}

export function boxTitle(box: WorkspaceBox): string {
  return box.kind === 'question' ? `${box.label} ${box.prompt}` : box.label;
}

export function humaniseKey(key: string): string {
  const words = key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}(T|$)/.test(value) && !Number.isNaN(new Date(value).getTime());
}

export function styleSummary(box: Pick<WorkspaceBox, 'records' | 'notices'> & { heard?: HeardWord[] }): string | null {
  const parts: string[] = [];
  const formats = box.records.filter((r) => r.kind === 'format').length;
  const proofs = box.records.filter((r) => r.kind === 'proof').length;
  const styles = box.records.length - formats - proofs;
  if (styles > 0) parts.push(pluralise(styles, 'style change'));
  if (formats > 0) parts.push(pluralise(formats, 'formatting change'));
  if (proofs > 0) parts.push(pluralise(proofs, 'proofreading change'));
  const likely = box.records.filter((r) => r.confidence === 'likely').length;
  if (likely > 0) parts.push(`${likely} worth checking`);
  if (box.notices.length > 0) parts.push(`${box.notices.length} noticed`);
  const heard = box.heard?.length ?? 0;
  if (heard > 0) parts.push(`${pluralise(heard, 'word')} heard uncertainly`);
  return parts.length > 0 ? parts.join(', ') : null;
}

/** The well's line for a doubtful word: the confidence as a percentage and the second of audio, never a guess at what was meant. */
export function heardSentence(h: HeardWord): string {
  const pct = `${Math.round(h.confidence * 100)} percent`;
  const at = `${Math.floor(h.audioStart / 60)}:${String(Math.floor(h.audioStart % 60)).padStart(2, '0')}`;
  return h.heard.toLowerCase() === h.word.toLowerCase() ? `heard at ${pct}, ${at} into the dictation` : `heard as "${h.heard}" at ${pct}, ${at} into the dictation`;
}

/** The Format button's announcement and status line (ADR-0002); the reason is the server's sentence, never the text. */
export function formatOutcomeSentence(outcome: 'applied' | 'rejected' | 'unchanged' | 'failed', reason?: string): string {
  switch (outcome) {
    case 'applied': return 'Formatted. One change record; Undo puts the dictated text back.';
    case 'rejected': return reason ?? 'The formatter\'s result was not applied.';
    case 'unchanged': return 'Nothing to format: the passage is already formatted.';
    default: return 'Could not format the text. Nothing changed.';
  }
}

export function timeOf(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
